import React, { useState, useCallback, useEffect } from "react";
import PropTypes from 'prop-types';
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Pencil, Settings, ToggleLeft, ToggleRight } from "lucide-react";
import Permiso from "../../router/Permiso";
import { MarcasService } from "../../services/MarcasService";
import { getBaseUrl } from "../../config/api";
import "./MarcasAdmin.css";

const ListadoMarcas = ({ token, debeRecargar, onRecargaCompleta }) => {
  const navigate = useNavigate();
  const baseUrl = getBaseUrl();
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [buscarMarca, setBuscarMarca] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRegistros, setTotalRegistros] = useState(0);

  const fetchMarcas = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await MarcasService.getMarcas({ token, pagina: currentPage, pageSize: 20, descripcion: buscarMarca });
      setMarcas(res.marcas);
      setTotalPages(res.totalPaginas);
      setTotalRegistros(res.totalRegistros);
    } catch (err) {
      console.error("Error al cargar las marcas:", err);
      setError("Error al cargar las marcas");
    } finally {
      setLoading(false);
    }
  }, [token, currentPage, buscarMarca]);

  useEffect(() => {
    fetchMarcas();
  }, [fetchMarcas]);

  useEffect(() => {
    if (debeRecargar) {
      fetchMarcas().then(() => {
        onRecargaCompleta();
      });
    }
  }, [debeRecargar, fetchMarcas, onRecargaCompleta]);

  const handleToggleEstado = async (marca) => {
    try {
      const result = await MarcasService.toggleEstadoMarca({ token, marca });
      fetchMarcas();
      toast.success(result.message);
    } catch (err) {
      toast.error("Error al cambiar el estado de la marca");
      console.error("Error:", err);
    }
  };

  const getEstadoClass = (estado) => {
    return estado === "ACTIVO" ? "estado-activo" : "estado-baja";
  };

  const handleUpdateMarca = async (marca) => {
    navigate(`/admin/marcas/${marca.id_marca}`);
  };

  return (
    <div className="marcas-admin-container">
      <div className="marcas-header">
        <h2>Lista de Marcas</h2>
      </div>

      <div className="usuarios-filtros">
        <div className="filtro-item">
          <label htmlFor="buscar">Buscar:</label>
          <input
            type="text"
            value={buscarMarca}
            onChange={(e) => setBuscarMarca(e.target.value)}
            placeholder="Ingrese descripción de la marca"
            className=""
          />
        </div>
        <div className="filtro-grupo">
          <button onClick={fetchMarcas} className="btn-buscar">Buscar</button>
        </div>
        <div className="filtro-grupo">
          <button onClick={() => setBuscarMarca("")} className="btn-limpiar-filtros">Limpiar Filtros</button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Cargando marcas...</p>
        </div>
      ) : (
        <>
          <div className="marcas-table-wrapper">
            <table className="marcas-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Imagen</th>
                  <th>Descripción</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {marcas.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="no-data">
                      <div className="no-data-content">
                        <span>{"\uD83D\uDC49"}</span>
                        <p>No hay marcas registradas</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  marcas.map((marca) => (
                    <tr key={marca.id_marca} className="marca-row">
                      <td className="marca-id">#{marca.id_marca}</td>
                      <td className="marca-imagen">
                        {marca.imagen ? (
                          <img src={`${baseUrl}/uploads/${marca.imagen}`} alt={marca.descripcion} className="marca-thumb" />
                        ) : (
                          <span className="no-image">Sin imagen</span>
                        )}
                      </td>
                      <td className="marca-descripcion">{marca.descripcion}</td>
                      <td className="marca-estado">
                        <span className={`estado-producto ${getEstadoClass(marca.estado)}`}>{marca.estado}</span>
                      </td>
                      <td className="marca-acciones">
                        <Permiso permiso="edit_marcas">
                          <button type="button" style={{ cursor: "pointer", border: "none", background: "none", padding: "6px", display: "inline-flex", verticalAlign: "middle" }} onClick={() => handleUpdateMarca(marca)} title="Editar">
                            <Pencil size={18} />
                          </button>
                        </Permiso>
                        <Permiso permiso="edit_parametros_marcas">
                          <button type="button" style={{ cursor: "pointer", border: "none", background: "none", padding: "6px", display: "inline-flex", verticalAlign: "middle", color: "#667eea" }} onClick={() => navigate(`/admin/marcas/${marca.id_marca}/parametros`)} title="Configurar Parámetros">
                            <Settings size={18} />
                          </button>
                        </Permiso>
                        <Permiso permiso="delete_marcas">
                          <button
                            onClick={() => handleToggleEstado(marca)}
                            className={`btn-toggle ${marca.estado === "ACTIVO" ? "btn-apagar" : "btn-encender"}`}
                            title={marca.estado === "ACTIVO" ? "Desactivar" : "Activar"}
                          >
                            {marca.estado === "ACTIVO" ? (
                              <ToggleLeft size={22} color="#e74c3c" />
                            ) : (
                              <ToggleRight size={22} color="#2ecc71" />
                            )}
                          </button>
                        </Permiso>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <span style={{ marginLeft: "12px", fontSize: "14px", color: "#555" }}> Total de registros: {totalRegistros} </span>
          <div className="pagination">
            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>Anterior</button>
            <span>Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>Siguiente</button>
          </div>
        </>
      )}
    </div>
  );
};

ListadoMarcas.propTypes = {
  token: PropTypes.string.isRequired,
  debeRecargar: PropTypes.bool,
  onRecargaCompleta: PropTypes.func
};

export default ListadoMarcas;
